import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import { LearningItem } from '../../models/LearningItem'
import { firstBookingForLearningItemForUserUuid } from "./bookingForLearningItem";
import { orderBy } from "lodash";

export function getEventAndDateFromLearningItem(learningItem: any, eventUuid: string, dateUuid: string) {
  const event = learningItem.scheduledCourse?.events.find((event: { uuid: string }) => event.uuid === eventUuid)

  const date = event?.dates.find((date: { uuid: string }) => date.uuid === dateUuid)

  return { event, date }
}

export function doesLearningItemHasTrainerOf(learningItem: AnonymousLearningItem, trainerUuid: string): boolean {
  if (learningItem.kind === 'scheduledCourse') {
    const trainer = learningItem.scheduledCourse.events
      .flatMap((event) => event.dates)
      .flatMap((date) => date.trainers || [])
      .find((trainer) => trainer.uuid === trainerUuid)

    return !!trainer
  }

  return false
}

export function getTrainerLearningItems(userLearningItems: LearningItem[], trainerUuid: string) {
  const trainerLearningItems = []

  for (const learningItem of userLearningItems) {
    if (learningItem.kind === 'scheduledCourse' && isUserTrainerForLearningItem(learningItem, trainerUuid)) {
      trainerLearningItems.push(learningItem)
    }
  }

  return trainerLearningItems
}

export function getTrainerFutureLearningItems(userLearningItems: LearningItem[], trainerUuid: string) {
  const trainerLearningItems = getTrainerLearningItems(userLearningItems, trainerUuid)

  return trainerLearningItems.filter((learningItem) => {
    return getNearestFutureEventDateForTrainer(learningItem, trainerUuid) !== null
  })
}

export function isUserTrainerForLearningItem(
  learningItem: Extract<LearningItem, { kind: 'scheduledCourse' }>,
  trainerUuid: string
) {
  return !!learningItem.scheduledCourse.events
    .flatMap((event) => event.dates)
    .flatMap((date) => date.trainers || [])
    .find((trainer) => trainer.uuid === trainerUuid)
}

export function sortTrainerLearningItemsByStartDate(
  trainerLearningItems: Extract<LearningItem, { kind: 'scheduledCourse' }>[],
  trainerUuid: string
) {
  return trainerLearningItems.sort((a, b) => {
    const dateA = getNearestFutureEventDateForTrainer(a, trainerUuid)
    const dateB = getNearestFutureEventDateForTrainer(b, trainerUuid)

    if (dateA === null && dateB === null) {
      // Handle cases where one or both dates are null
      return 0
    }
    if (dateA === null) {
      // Place items without dates at the end
      return 1
    }
    if (dateB === null) {
      return -1
    }

    return dateA.getTime() - dateB.getTime()
  })
}

export function getNearestFutureEventDateForTrainer(
  learningItem: Extract<LearningItem, { kind: 'scheduledCourse' }>,
  trainerUuid: string
): Date | null {
  const now = new Date()

  const dates = learningItem.scheduledCourse.events.flatMap((event) => event.dates)

  const futureDates = dates
    .filter((date) => date.trainers?.find((trainer) => trainer.uuid === trainerUuid))
    .map((date) => new Date(date.startDate))
    .filter((date) => date > now)

  if (futureDates.length === 0) {
    return null
  }

  return futureDates.reduce((earliest, date) => (date < earliest ? date : earliest))
}

export function filterLearningItemsByKind(userLearningItems: LearningItem[], kinds: string[]) {
  return userLearningItems
    .filter((learningItem) => kinds.includes(learningItem.kind))
}
